import { useEffect, useMemo, useState } from "react";
import {
  Search, Bell, HelpCircle, ChevronDown, Filter, Calendar, CheckCircle2, AlertCircle,
  Clock, MoreVertical, Loader2, ChevronLeft, ChevronRight, TrendingUp, X, Eye, Download,
  CreditCard, ReceiptText, RotateCcw,
} from "lucide-react";
import toast from "react-hot-toast";
import { axiosInstance } from "../lib/axios";
import { useAuthStore } from "../store/useAuthStore";
import ClientSidebar from "../components/portal/ClientSidebar";
import ClientTopBar from "../components/portal/ClientTopBar";

interface LineItem { description: string; quantity: number; rate: number; discount: number }
interface Invoice {
  _id: string; invoiceNumber: string; projectName: string; projectStatus: string;
  milestoneTitle: string; amount: number; dueDate?: string; status: string;
  bucket: "paid" | "upcoming" | "pending" | "overdue"; paymentMethod: string; paidAt?: string;
  createdAt?: string; lineItems: LineItem[];
}
interface InvoiceData { invoices: Invoice[] }

const money = (n: number) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

const DATE_RANGES = ["All Time", "Today", "Last 7 Days", "Last 30 Days", "Last 90 Days", "This Month", "Last Month", "This Quarter", "This Year", "Last Year"];
const rangeStart = (r: string): Date | null => {
  const now = new Date();
  const d0 = (dt: Date) => new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  switch (r) {
    case "Today": return d0(now);
    case "Last 7 Days": return new Date(now.getTime() - 7 * 86400000);
    case "Last 30 Days": return new Date(now.getTime() - 30 * 86400000);
    case "Last 90 Days": return new Date(now.getTime() - 90 * 86400000);
    case "This Month": return new Date(now.getFullYear(), now.getMonth(), 1);
    case "Last Month": return new Date(now.getFullYear(), now.getMonth() - 1, 1);
    case "This Quarter": return new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    case "This Year": return new Date(now.getFullYear(), 0, 1);
    case "Last Year": return new Date(now.getFullYear() - 1, 0, 1);
    default: return null;
  }
};
const rangeEnd = (r: string): Date | null => {
  const now = new Date();
  if (r === "Last Month") return new Date(now.getFullYear(), now.getMonth(), 1);
  if (r === "Last Year") return new Date(now.getFullYear(), 0, 1);
  return null;
};

const chipStyle = (b: string) => {
  if (b === "paid") return "bg-primary-container text-on-primary-container";
  if (b === "pending") return "bg-secondary-container text-on-secondary-container";
  if (b === "overdue") return "bg-error-container text-on-error-container";
  return "bg-surface-container text-on-surface-variant";
};
const dueSub = (inv: Invoice) => {
  if (inv.bucket === "paid") return { text: inv.paidAt ? `Paid ${fmtDate(inv.paidAt)}` : "Paid", cls: "text-secondary" };
  if (!inv.dueDate) return { text: "", cls: "" };
  const days = Math.ceil((new Date(inv.dueDate).getTime() - Date.now()) / 86400000);
  if (inv.bucket === "overdue" || days < 0) return { text: `${Math.abs(days)} days overdue`, cls: "text-error" };
  if (days <= 3) return { text: `Due in ${days} day${days === 1 ? "" : "s"}`, cls: "text-error" };
  return { text: "Upcoming", cls: "text-on-surface-variant" };
};
const payMethodLabel = (inv: Invoice) => {
  if (inv.bucket === "paid") return inv.paymentMethod || "—";
  if (inv.bucket === "upcoming") return "Available When Due";
  return "Not Paid";
};

const ClientInvoicesPage = () => {
  const { authUser } = useAuthStore();
  const [data, setData] = useState<InvoiceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [projectFilter, setProjectFilter] = useState("All");
  const [dateRange, setDateRange] = useState("All Time");
  const [statusFilter, setStatusFilter] = useState<"all" | "paid" | "upcoming" | "pending" | "overdue">("all");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [detail, setDetail] = useState<Invoice | null>(null);
  const [rowMenu, setRowMenu] = useState<string | null>(null);

  useEffect(() => {
    axiosInstance.get<InvoiceData>("/client-portal/invoices")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const all = data?.invoices || [];
  const projects = useMemo(() => ["All", ...Array.from(new Set(all.map((i) => i.projectName)))], [all]);

  // Scoped by search + project + date range — drives the KPIs and the trend.
  const scoped = useMemo(() => {
    const start = rangeStart(dateRange), end = rangeEnd(dateRange);
    const q = search.trim().toLowerCase();
    return all.filter((i) => {
      if (projectFilter !== "All" && i.projectName !== projectFilter) return false;
      if (q && ![i.invoiceNumber, i.projectName, i.milestoneTitle].join(" ").toLowerCase().includes(q)) return false;
      if (start) {
        const d = new Date(i.createdAt || i.dueDate || 0).getTime();
        if (d < start.getTime()) return false;
        if (end && d >= end.getTime()) return false;
      }
      return true;
    });
  }, [all, search, projectFilter, dateRange]);

  // Table rows = scoped + status filter (from KPI card clicks).
  const rows = useMemo(() => (statusFilter === "all" ? scoped : scoped.filter((i) => i.bucket === statusFilter)), [scoped, statusFilter]);

  // KPIs + trend recomputed from scoped so all filters update everything.
  const summary = useMemo(() => {
    const mk = () => ({ count: 0, amount: 0, projects: new Set<string>() });
    const acc: any = { paid: mk(), upcoming: mk(), pending: mk(), overdue: mk() };
    scoped.forEach((i) => { const b = acc[i.bucket]; b.count++; b.amount += i.amount; if (i.projectName !== "—") b.projects.add(i.projectName); });
    const out: any = {};
    for (const k of Object.keys(acc)) out[k] = { count: acc[k].count, amount: acc[k].amount, projects: acc[k].projects.size };
    return out;
  }, [scoped]);
  const totalSpent = summary.paid.amount;

  const trend = useMemo(() => {
    const byMonth: Record<string, number> = {};
    scoped.filter((i) => i.bucket === "paid").forEach((i) => {
      const d = i.paidAt || i.dueDate || i.createdAt; if (!d) return;
      const dt = new Date(d); byMonth[`${dt.getFullYear()}-${dt.getMonth()}`] = (byMonth[`${dt.getFullYear()}-${dt.getMonth()}`] || 0) + i.amount;
    });
    const now = new Date(); const out: { month: string; amount: number }[] = []; let cum = 0;
    for (let k = 7; k >= 0; k--) {
      const d = new Date(now.getFullYear(), now.getMonth() - k, 1);
      cum += byMonth[`${d.getFullYear()}-${d.getMonth()}`] || 0;
      out.push({ month: d.toLocaleString("en-US", { month: "short" }), amount: cum });
    }
    return out;
  }, [scoped]);
  const pctChange = useMemo(() => {
    const l = trend[trend.length - 1]?.amount || 0, p = trend[trend.length - 2]?.amount || 0;
    return p ? Math.round(((l - p) / p) * 100) : 0;
  }, [trend]);

  const pageCount = Math.max(1, Math.ceil(rows.length / perPage));
  const pageRows = rows.slice((page - 1) * perPage, page * perPage);
  useEffect(() => { setPage(1); }, [search, projectFilter, dateRange, statusFilter, perPage]);

  const reset = () => { setSearch(""); setProjectFilter("All"); setDateRange("All Time"); setStatusFilter("all"); };
  const filtersActive = search || projectFilter !== "All" || dateRange !== "All Time" || statusFilter !== "all";

  const download = (inv: Invoice) => {
    const w = window.open("", "_blank", "width=760,height=900");
    if (!w) return toast.error("Allow popups to download");
    const items = inv.lineItems.length ? inv.lineItems : [{ description: inv.milestoneTitle || "Invoice amount", quantity: 1, rate: inv.amount, discount: 0 }];
    const rowsHtml = items.map((li) => `<tr><td style="padding:8px;border-bottom:1px solid #eee">${li.description || "Item"}</td><td style="padding:8px;border-bottom:1px solid #eee;text-align:center">${li.quantity}</td><td style="padding:8px;border-bottom:1px solid #eee;text-align:right">$${li.rate.toLocaleString()}</td><td style="padding:8px;border-bottom:1px solid #eee;text-align:right">$${((li.quantity * li.rate) - li.discount).toLocaleString()}</td></tr>`).join("");
    w.document.write(`<!doctype html><html><head><title>${inv.invoiceNumber}</title></head><body style="font-family:Arial,sans-serif;padding:40px;color:#1f2937">
      <div style="display:flex;justify-content:space-between;align-items:flex-start">
        <div><h1 style="color:${(authUser as any)?.tenant?.primaryColor || "#0058c3"};margin:0">${(authUser as any)?.tenant?.companyName || "Omni Workspace"}</h1><p style="color:#6b7280;margin:2px 0">${(authUser as any)?.tenant ? "" : "by DymaxTech"}</p></div>
        <div style="text-align:right"><h2 style="margin:0">INVOICE</h2><p style="margin:2px 0;color:#6b7280">${inv.invoiceNumber}</p></div>
      </div><hr style="margin:20px 0;border:none;border-top:1px solid #e5e7eb">
      <table style="width:100%;font-size:14px"><tr><td><strong>Bill To:</strong><br>${authUser?.fullName || "Client"}</td>
      <td style="text-align:right"><strong>Project:</strong> ${inv.projectName}<br><strong>Milestone:</strong> ${inv.milestoneTitle || "—"}<br><strong>Generated:</strong> ${fmtDate(inv.createdAt)}<br><strong>Due:</strong> ${fmtDate(inv.dueDate)}<br><strong>Status:</strong> ${inv.status}</td></tr></table>
      <table style="width:100%;border-collapse:collapse;margin-top:24px;font-size:14px"><thead><tr style="background:#eff4ff"><th style="padding:8px;text-align:left">Description</th><th style="padding:8px">Qty</th><th style="padding:8px;text-align:right">Rate</th><th style="padding:8px;text-align:right">Amount</th></tr></thead><tbody>${rowsHtml}</tbody></table>
      <div style="text-align:right;margin-top:16px;font-size:16px"><strong>Total: ${money(inv.amount)}</strong></div>
      <p style="color:#6b7280;font-size:12px;margin-top:40px">Thank you for your business. Payment terms as agreed.</p></body></html>`);
    w.document.close(); w.focus(); w.print();
  };

  return (
    <div className="min-h-screen bg-[#f8f9ff] text-on-surface flex" onClick={() => setRowMenu(null)}>
      <ClientSidebar active="invoices" />
      <div className="flex-1 min-w-0">
        <ClientTopBar title="Invoices" />

        {loading ? (
          <div className="grid place-items-center h-[calc(100vh-4rem)]"><Loader2 className="animate-spin text-primary" size={36} /></div>
        ) : (
        <main className="p-4 lg:p-8 max-w-[1400px] mx-auto space-y-6">
          <div>
            <h1 className="text-[26px] font-bold text-on-surface">Invoices</h1>
            <p className="text-[14px] text-on-surface-variant">Manage and review all invoices associated with your projects.</p>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 bg-white p-2 border border-outline-variant rounded-xl shadow-sm">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-outline" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search invoices, projects, or milestones..." className="w-full pl-11 pr-3 py-2.5 bg-transparent text-[14px] outline-none" />
            </div>
            <div className="hidden sm:block w-px bg-outline-variant self-stretch my-1" />
            <div className="flex items-center gap-1 flex-wrap pr-1">
              <Dropdown value={projectFilter} onChange={setProjectFilter} options={projects} render={(p) => (p === "All" ? "All Projects" : p)} icon={<Filter size={15} />} />
              <Dropdown value={dateRange} onChange={setDateRange} options={DATE_RANGES} render={(d) => d} icon={<Calendar size={15} />} />
              {filtersActive && (
                <button onClick={reset} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-semibold text-primary hover:bg-primary/5"><RotateCcw size={14} /> Reset</button>
              )}
            </div>
          </div>

          {/* Financial overview — KPI summary is the primary focal point (left,
              larger); the Amount Spent Trend graph is secondary (right). */}
          <div className="flex flex-col lg:flex-row gap-6 items-stretch">
            {/* LEFT — KPI Summary (dominant) */}
            <div className="lg:w-[58%] grid grid-cols-2 gap-4 content-start">
              <SummaryCard label="Paid" icon={CheckCircle2} iconCls="text-primary" active={statusFilter === "paid"}
                onClick={() => setStatusFilter(statusFilter === "paid" ? "all" : "paid")}
                count={summary.paid.count} amount={money(summary.paid.amount)} amountLabel="Paid" amountCls="text-primary" projects={summary.paid.projects} />
              <SummaryCard label="Upcoming" icon={Calendar} iconCls="text-outline" active={statusFilter === "upcoming"}
                onClick={() => setStatusFilter(statusFilter === "upcoming" ? "all" : "upcoming")}
                count={summary.upcoming.count} amount={money(summary.upcoming.amount)} amountLabel="Scheduled" amountCls="text-on-surface" projects={summary.upcoming.projects} />
              <div className="col-span-2 bg-white p-5 rounded-xl border border-outline-variant shadow-sm">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[11px] font-bold text-tertiary uppercase tracking-widest">Action Required</span>
                  <AlertCircle size={18} className="text-error" />
                </div>
                <div className="flex justify-between items-stretch">
                  <button onClick={() => setStatusFilter(statusFilter === "overdue" ? "all" : "overdue")} className={`flex-1 flex flex-col gap-1 text-left rounded-lg p-2 -m-1 ${statusFilter === "overdue" ? "ring-2 ring-error/40" : "hover:bg-surface-container-low"}`}>
                    <div className="flex items-center gap-1.5"><AlertCircle size={14} className="text-error" /><span className="text-[11px] font-bold text-error uppercase">Overdue</span></div>
                    <div className="text-[18px] font-bold text-on-surface">{summary.overdue.count} Invoices</div>
                    <div className="text-[14px] font-bold text-error">{money(summary.overdue.amount)} Outstanding</div>
                    <div className="text-[11px] text-on-surface-variant">Across {summary.overdue.projects} Project{summary.overdue.projects === 1 ? "" : "s"}</div>
                  </button>
                  <div className="w-px bg-outline-variant/40 mx-3" />
                  <button onClick={() => setStatusFilter(statusFilter === "pending" ? "all" : "pending")} className={`flex-1 flex flex-col gap-1 text-left rounded-lg p-2 -m-1 ${statusFilter === "pending" ? "ring-2 ring-secondary/40" : "hover:bg-surface-container-low"}`}>
                    <div className="flex items-center gap-1.5"><Clock size={14} className="text-secondary" /><span className="text-[11px] font-bold text-secondary uppercase">Pending</span></div>
                    <div className="text-[18px] font-bold text-on-surface">{summary.pending.count} Invoices</div>
                    <div className="text-[14px] font-bold text-secondary">{money(summary.pending.amount)} Outstanding</div>
                    <div className="text-[11px] text-on-surface-variant">Across {summary.pending.projects} Project{summary.pending.projects === 1 ? "" : "s"}</div>
                  </button>
                </div>
              </div>
            </div>

            {/* RIGHT — Amount Spent Trend (secondary) */}
            <div className="lg:w-[42%] bg-white p-5 rounded-xl border border-outline-variant shadow-sm flex flex-col">
              <div>
                <h3 className="text-[12px] font-semibold text-tertiary uppercase tracking-wider mb-2">Amount Spent Trend</h3>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="text-[30px] font-bold text-on-surface">{money(totalSpent)}</span>
                  {pctChange !== 0 && (
                    <span className={`text-[13px] font-bold flex items-center gap-1 ${pctChange >= 0 ? "text-secondary" : "text-error"}`}>
                      <TrendingUp size={14} /> {pctChange > 0 ? "+" : ""}{pctChange}% <span className="text-on-surface-variant/60 font-normal">vs prev period</span>
                    </span>
                  )}
                </div>
              </div>
              <TrendChart trend={trend} />
            </div>
          </div>

          {statusFilter !== "all" && (
            <div className="flex items-center gap-2 text-[13px] text-on-surface-variant">
              Filtered by status: <span className="font-semibold capitalize text-on-surface">{statusFilter}</span>
              <button onClick={() => setStatusFilter("all")} className="text-primary font-semibold hover:underline">clear</button>
            </div>
          )}

          {/* Table */}
          <div className="bg-white border border-outline-variant rounded-xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-surface-container-low">
                  <tr className="text-[12px] text-tertiary uppercase">
                    <Th>Invoice</Th><Th>Project</Th><Th>Milestone</Th><Th>Amount</Th><Th>Due Date</Th><Th>Status</Th><Th>Payment Method</Th><Th align="right">Action</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant">
                  {pageRows.length === 0 ? (
                    <tr><td colSpan={8} className="px-6 py-12 text-center text-outline">No invoices found.</td></tr>
                  ) : pageRows.map((inv) => {
                    const sub = dueSub(inv);
                    return (
                      <tr key={inv._id} className="hover:bg-surface-container-low transition-colors">
                        <td className="px-5 py-4">
                          <button onClick={() => setDetail(inv)} className="text-[14px] font-semibold text-primary hover:underline">{inv.invoiceNumber}</button>
                          <div className="text-[11px] text-on-surface-variant">Generated {fmtDate(inv.createdAt)}</div>
                        </td>
                        <td className="px-5 py-4"><div className="text-[14px] text-on-surface">{inv.projectName}</div><div className="text-[11px] text-on-surface-variant">{inv.projectStatus || "—"}</div></td>
                        <td className="px-5 py-4"><div className="text-[14px] text-on-surface">{inv.milestoneTitle || "Not Linked"}</div>{!inv.milestoneTitle && <div className="text-[11px] text-on-surface-variant">Custom Invoice</div>}</td>
                        <td className="px-5 py-4"><div className="text-[14px] font-bold text-on-surface">{money(inv.amount)}</div></td>
                        <td className="px-5 py-4"><div className="text-[14px] text-on-surface">{fmtDate(inv.dueDate)}</div>{sub.text && <div className={`text-[11px] font-semibold ${sub.cls}`}>{sub.text}</div>}</td>
                        <td className="px-5 py-4"><span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${chipStyle(inv.bucket)}`}>{inv.status}</span></td>
                        <td className="px-5 py-4 text-[14px] text-on-surface-variant">{payMethodLabel(inv)}</td>
                        <td className="px-5 py-4 text-right relative">
                          <button onClick={(e) => { e.stopPropagation(); setRowMenu(rowMenu === inv._id ? null : inv._id); }} className="text-outline hover:text-primary p-1"><MoreVertical size={18} /></button>
                          {rowMenu === inv._id && (
                            <div className="absolute right-5 top-12 z-20 w-48 bg-white rounded-lg border border-outline-variant shadow-lg overflow-hidden text-left" onClick={(e) => e.stopPropagation()}>
                              <MenuItem icon={Eye} label="View Invoice" onClick={() => { setRowMenu(null); setDetail(inv); }} />
                              <MenuItem icon={Download} label="Download Invoice" onClick={() => { setRowMenu(null); download(inv); }} />
                              {(inv.bucket === "pending" || inv.bucket === "overdue") && (
                                <MenuItem icon={CreditCard} label="Pay Invoice" onClick={() => { setRowMenu(null); toast("Online payment (Stripe / PayPal) is being integrated.", { icon: "💳" }); }} />
                              )}
                              {inv.bucket === "paid" && (
                                <MenuItem icon={ReceiptText} label="View Receipt" onClick={() => { setRowMenu(null); setDetail(inv); }} />
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-3 bg-surface-container-low border-t border-outline-variant flex flex-wrap items-center justify-between gap-3">
              <div className="text-[13px] text-on-surface-variant">Showing <span className="font-bold text-on-surface">{rows.length === 0 ? 0 : (page - 1) * perPage + 1}–{Math.min(page * perPage, rows.length)}</span> of <span className="font-bold text-on-surface">{rows.length}</span> invoices</div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2"><span className="text-[11px] text-tertiary uppercase">Rows:</span>
                  <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))} className="bg-white border border-outline-variant rounded px-2 py-1 text-[13px] font-semibold outline-none"><option>10</option><option>25</option><option>50</option></select>
                </div>
                <div className="flex items-center gap-1">
                  <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="p-1 rounded hover:bg-surface-container-high text-on-surface-variant disabled:opacity-30"><ChevronLeft size={18} /></button>
                  <span className="text-[13px] font-semibold text-on-surface px-2">{page} / {pageCount}</span>
                  <button disabled={page >= pageCount} onClick={() => setPage((p) => p + 1)} className="p-1 rounded hover:bg-surface-container-high text-on-surface-variant disabled:opacity-30"><ChevronRight size={18} /></button>
                </div>
              </div>
            </div>
          </div>
        </main>
        )}
      </div>

      {detail && <InvoiceDetailModal invoice={detail} clientName={authUser?.fullName} onClose={() => setDetail(null)} onDownload={() => download(detail)} />}
    </div>
  );
};

const Th = ({ children, align }: { children: React.ReactNode; align?: "right" }) => (
  <th className={`px-5 py-3.5 font-semibold whitespace-nowrap ${align === "right" ? "text-right" : ""}`}>{children}</th>
);
const MenuItem = ({ icon: Icon, label, onClick }: any) => (
  <button onClick={onClick} className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-on-surface hover:bg-surface-container-low"><Icon size={15} /> {label}</button>
);
const Dropdown = ({ value, onChange, options, render, icon }: {
  value: string; onChange: (v: string) => void; options: string[]; render: (o: string) => string; icon: React.ReactNode;
}) => (
  <div className="relative">
    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-outline pointer-events-none">{icon}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="appearance-none bg-transparent pl-9 pr-8 py-2 rounded-lg text-[14px] font-semibold text-on-surface hover:bg-surface-container-low cursor-pointer outline-none">
      {options.map((o) => <option key={o} value={o}>{render(o)}</option>)}
    </select>
    <ChevronDown size={15} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-outline" />
  </div>
);
const SummaryCard = ({ label, icon: Icon, iconCls, count, amount, amountLabel, amountCls, projects, onClick, active }: any) => (
  <button onClick={onClick} className={`bg-white p-5 rounded-xl border shadow-sm flex flex-col justify-between text-left transition-all ${active ? "border-primary ring-2 ring-primary/30" : "border-outline-variant hover:border-primary/40"}`}>
    <div>
      <div className="flex justify-between items-center mb-2"><span className="text-[11px] font-bold text-tertiary uppercase tracking-widest">{label}</span><Icon size={20} className={iconCls} /></div>
      <div className="text-[26px] leading-tight font-bold text-on-surface">{count} <span className="text-[16px] font-semibold text-on-surface-variant">Invoices</span></div>
    </div>
    <div className="mt-3"><div className={`text-[15px] font-bold ${amountCls}`}>{amount} {amountLabel}</div><div className="text-[11px] text-on-surface-variant">Across {projects} Project{projects === 1 ? "" : "s"}</div></div>
  </button>
);

const TrendChart = ({ trend }: { trend: { month: string; amount: number }[] }) => {
  const [hover, setHover] = useState<number | null>(null);
  if (!trend.length || trend.every((t) => t.amount === 0)) return <div className="flex-1 min-h-[160px] grid place-items-center text-outline text-sm">No spending in this period.</div>;
  const W = 800, H = 200, pad = 20;
  const max = Math.max(1, ...trend.map((t) => t.amount));
  const stepX = trend.length > 1 ? W / (trend.length - 1) : W;
  const pts = trend.map((t, i) => ({ x: i * stepX, y: H - pad - (t.amount / max) * (H - pad * 2), ...t }));
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(0)},${p.y.toFixed(0)}`).join(" ");
  const area = `${line} L${W},${H} L0,${H} Z`;
  return (
    <div className="flex-1 flex flex-col mt-4">
      <div className="flex-1 min-h-[160px] relative">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-full">
          <defs><linearGradient id="invGrad" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#0070F3" stopOpacity="0.3" /><stop offset="100%" stopColor="#0070F3" stopOpacity="0" /></linearGradient></defs>
          {[0, 1, 2, 3, 4].map((g) => <line key={g} x1="0" x2={W} y1={(H / 4) * g} y2={(H / 4) * g} stroke="#c1c6d7" strokeOpacity="0.15" />)}
          <path d={area} fill="url(#invGrad)" />
          <path d={line} fill="none" stroke="#0070F3" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          {pts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={hover === i ? 6 : 4} fill="#0070F3" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} style={{ cursor: "pointer" }} />)}
        </svg>
        {hover !== null && pts[hover] && (
          <div className="absolute -translate-x-1/2 -translate-y-full bg-white border border-outline-variant rounded-lg shadow px-2.5 py-1.5 text-[11px] pointer-events-none"
            style={{ left: `${(pts[hover].x / W) * 100}%`, top: `${(pts[hover].y / H) * 100}%` }}>
            <div className="font-bold text-on-surface">{money(pts[hover].amount)}</div>
            <div className="text-on-surface-variant">{pts[hover].month} · cumulative</div>
          </div>
        )}
      </div>
      <div className="flex justify-between px-1 pt-3 text-[11px] text-on-surface-variant">{trend.map((t, i) => <span key={i}>{t.month}</span>)}</div>
    </div>
  );
};

// ── Invoice / Receipt detail modal ───────────────────────────────────────────
const InvoiceDetailModal = ({ invoice, clientName, onClose, onDownload }: { invoice: Invoice; clientName?: string | undefined; onClose: () => void; onDownload: () => void }) => {
  const isReceipt = invoice.bucket === "paid";
  const items = invoice.lineItems.length ? invoice.lineItems : [{ description: invoice.milestoneTitle || "Invoice amount", quantity: 1, rate: invoice.amount, discount: 0 }];
  const subtotal = items.reduce((s, li) => s + li.quantity * li.rate, 0);
  const discount = items.reduce((s, li) => s + li.discount, 0);
  const paid = isReceipt ? invoice.amount : 0;
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-outline-variant flex items-start justify-between">
          <div>
            <h2 className="text-[18px] font-bold text-on-surface">{isReceipt ? "Receipt" : "Invoice"} {invoice.invoiceNumber}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold ${chipStyle(invoice.bucket)}`}>{invoice.status}</span>
              <span className="text-[13px] font-bold text-on-surface">{money(invoice.amount)}</span>
              <span className="text-[12px] text-on-surface-variant">Due {fmtDate(invoice.dueDate)}</span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-surface-container-low text-on-surface-variant"><X size={18} /></button>
        </div>
        <div className="p-6 overflow-y-auto space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <Info label="Invoice Date" value={fmtDate(invoice.createdAt)} />
            <Info label="Due Date" value={fmtDate(invoice.dueDate)} />
            <Info label="Project" value={invoice.projectName} />
            <Info label="Milestone" value={invoice.milestoneTitle || "Not linked"} />
            <Info label="Bill To" value={clientName || "Client"} />
            <Info label="Status" value={invoice.status} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-outline mb-2">Invoice Breakdown</p>
            <table className="w-full text-[13px]">
              <thead><tr className="text-[11px] text-outline uppercase border-b border-outline-variant"><td className="py-1.5">Description</td><td className="py-1.5 text-center">Qty</td><td className="py-1.5 text-right">Rate</td><td className="py-1.5 text-right">Amount</td></tr></thead>
              <tbody>
                {items.map((li, i) => (
                  <tr key={i} className="border-b border-outline-variant/50"><td className="py-2 text-on-surface">{li.description || "Item"}</td><td className="py-2 text-center text-on-surface-variant">{li.quantity}</td><td className="py-2 text-right text-on-surface-variant">{money(li.rate)}</td><td className="py-2 text-right font-semibold text-on-surface">{money(li.quantity * li.rate - li.discount)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="rounded-lg bg-surface-container-low p-4 space-y-1.5">
            <Row label="Subtotal" value={money(subtotal)} />
            {discount > 0 && <Row label="Discount" value={`- ${money(discount)}`} />}
            <div className="border-t border-outline-variant pt-1.5"><Row label="Total Amount" value={money(invoice.amount)} bold /></div>
            <Row label="Amount Paid" value={money(paid)} />
            <Row label="Remaining Balance" value={money(invoice.amount - paid)} />
            {isReceipt && <><Row label="Payment Method" value={invoice.paymentMethod || "—"} /><Row label="Payment Date" value={fmtDate(invoice.paidAt)} /></>}
          </div>
        </div>
        <div className="p-4 border-t border-outline-variant bg-surface-container-low/50 flex justify-end gap-2">
          <button onClick={onDownload} className="px-4 py-2 rounded-lg border border-outline-variant text-[13px] font-semibold text-on-surface hover:bg-white inline-flex items-center gap-2"><Download size={15} /> Download {isReceipt ? "Receipt" : "Invoice"}</button>
          {(invoice.bucket === "pending" || invoice.bucket === "overdue") && (
            <button onClick={() => toast("Online payment (Stripe / PayPal) is being integrated.", { icon: "💳" })} className="px-5 py-2 rounded-lg bg-primary text-white text-[13px] font-bold hover:bg-[#004b9e] inline-flex items-center gap-2"><CreditCard size={15} /> Pay Invoice</button>
          )}
        </div>
      </div>
    </div>
  );
};
const Info = ({ label, value }: { label: string; value: string }) => (
  <div><p className="text-[10px] uppercase tracking-wider font-bold text-outline">{label}</p><p className="text-[13px] font-semibold text-on-surface">{value || "—"}</p></div>
);
const Row = ({ label, value, bold }: { label: string; value: string; bold?: boolean }) => (
  <div className="flex justify-between text-[13px]"><span className={bold ? "font-bold text-on-surface" : "text-on-surface-variant"}>{label}</span><span className={bold ? "font-bold text-on-surface" : "text-on-surface"}>{value}</span></div>
);

export default ClientInvoicesPage;
